import { useState, useEffect } from 'react';

export const useLoader = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Simulate loading progress
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      return () => clearInterval(interval);
    }
  }, [isLoading]);

  // Complete loading after a minimum time
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Minimum 2 seconds to show loader

    return () => clearTimeout(timer);
  }, []);

  // Function to manually complete loading
  const completeLoading = () => {
    setIsLoading(false);
    setLoadingProgress(100);
  };

  // Function to reset loading state
  const resetLoading = () => {
    setIsLoading(true);
    setLoadingProgress(0);
  };

  return {
    isLoading,
    loadingProgress,
    completeLoading,
    resetLoading
  };
};

// Hook for image loading
export const useImageLoader = (imageUrls = []) => {
  const [loadedImages, setLoadedImages] = useState(0);
  const [totalImages, setTotalImages] = useState(0);

  useEffect(() => {
    if (imageUrls.length === 0) return;

    setTotalImages(imageUrls.length);
    setLoadedImages(0);

    const loadImage = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject();
        img.src = url;
      });
    };

    const loadAllImages = async () => {
      try {
        await Promise.allSettled(imageUrls.map(loadImage));
        setLoadedImages(totalImages);
      } catch (error) {
        console.error('Error loading images:', error);
        setLoadedImages(totalImages); // Mark as complete even if some fail
      }
    };

    loadAllImages();
  }, [imageUrls]);

  const loadingProgress = totalImages > 0 ? (loadedImages / totalImages) * 100 : 0;
  const isComplete = loadedImages === totalImages && totalImages > 0;

  return {
    loadedImages,
    totalImages,
    loadingProgress,
    isComplete
  };
};
